import React, { useMemo, useState } from "react";
import { Alert, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useFormikContext } from "formik";

const FormSection = ({ title, level = 1, children }) => {
    const Tag = `h${Math.min(6, level + 3)}`;

    return (
        <section className="mb-4 form-section">
            <Tag
                className={`text-muted ${level === 1 ? "fw-semibold" : "fw-medium"
                    }`}
            >
                {title}
            </Tag>
            {children}
        </section>
    );
};

const decimalOrEmpty = (raw) => {
    if (raw === "") return "";

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : "";
};

const centsToAmount = (value) => {
    if (value === null || value === undefined || value === "") return "";

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed / 100 : "";
};

const amountToCents = (value) => {
    if (value === "") return "";

    const parsed = Number(String(value).replace(",", "."));
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : "";
};

const CentsInput = ({ value, onChange, disabled = false, placeholder = "" }) => {
    const [draft, setDraft] = useState(null);
    const shownValue = draft !== null ? draft : centsToAmount(value);

    return (
        <Form.Control
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={shownValue}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
                if (draft === null) return;

                onChange(amountToCents(draft));
                setDraft(null);
            }}
        />
    );
};

const CDW_CONFIG = [
    {
        key: "cdw",
        suffix: 1,
        name: "CDW (Collision Damage Waiver)",
        subtitle: "Cuts down damage costs.",
    },
    {
        key: "cdw2",
        suffix: 2,
        name: "CDW2 (VIP Zero)",
        subtitle: "Premium crash protection.",
    },
];

const REDUCTION_FIELDS = [
    {
        label: "Security Deposit",
        rentalPolicyField: "deposit",
        discountedPrefix: "discountedDepositExtra",
    },
    {
        label: "Damage Excess",
        rentalPolicyField: "damageExcess",
        discountedPrefix: "discountedDamageExcess",
    },
    {
        label: "Theft Excess",
        rentalPolicyField: "theftExcess",
        discountedPrefix: "discountedTheftExcess",
    },
];

const getExtraKey = (row) =>
    String(row?.Extra?.key ?? row?.key ?? "")
        .trim()
        .toLowerCase();

const isFilledNumber = (value) =>
    value !== "" &&
    value !== null &&
    value !== undefined &&
    Number.isFinite(Number(value));

export default function ExcessDepositTab({ meta = {} }) {
    const { values, setFieldValue, submitCount } = useFormikContext();

    const extras = Array.isArray(values.ShopMotorcycleExtra)
        ? values.ShopMotorcycleExtra
        : [];

    const rentalPolicy = values.rentalPolicy || {};

    const availableExtraDefinitions = useMemo(() => {
        const candidates = [
            ...(Array.isArray(meta.extras) ? meta.extras : []),
            ...(Array.isArray(meta.shopExtras) ? meta.shopExtras : []),
            ...(Array.isArray(meta.ShopMotorcycleExtra)
                ? meta.ShopMotorcycleExtra
                : []),
            ...extras,
        ];

        return candidates.reduce((map, item) => {
            const extra = item?.Extra || item;
            const key = String(extra?.key ?? "")
                .trim()
                .toLowerCase();

            if (key && !map.has(key)) {
                map.set(key, item);
            }

            return map;
        }, new Map());
    }, [meta, extras]);

    const updateExtras = (nextExtras) => {
        setFieldValue("ShopMotorcycleExtra", nextExtras);
    };

    const findExtraIndex = (key) =>
        extras.findIndex((item) => getExtraKey(item) === key);

    const buildExtra = (config) => {
        const { key } = config;
        const template = availableExtraDefinitions.get(key);
        const templateExtra = template?.Extra || template || {};

        return {
            ...(template?.shopExtraId
                ? { shopExtraId: template.shopExtraId }
                : {}),
            ...(template?.extraId || templateExtra?.id
                ? { extraId: template?.extraId || templateExtra.id }
                : {}),
            isRequired: false,
            description: template?.description ?? null,
            pricePerDay: "",
            maxPrice: 0,
            priceType: "PER_DAY",
            Extra: {
                ...templateExtra,
                key,
            },
        };
    };

    const setEnabled = (config, enabled) => {
        const index = findExtraIndex(config.key);

        if (enabled && index < 0) {
            updateExtras([...extras, buildExtra(config)]);
            return;
        }

        if (enabled && index >= 0) {
            setFieldValue(
                `ShopMotorcycleExtra.${index}.priceType`,
                "PER_DAY"
            );
            return;
        }

        if (!enabled && index >= 0) {
            updateExtras(
                extras.filter((_, itemIndex) => itemIndex !== index)
            );
            setFieldValue(
                `discountedDepositExtra${config.suffix}`,
                null
            );
            setFieldValue(
                `discountedDamageExcess${config.suffix}`,
                null
            );
            setFieldValue(
                `discountedTheftExcess${config.suffix}`,
                null
            );
        }
    };

    const setExtraField = (key, field, value) => {
        const index = findExtraIndex(key);
        if (index < 0) return;

        setFieldValue(`ShopMotorcycleExtra.${index}.${field}`, value);
    };

    const baseComplete = [
        rentalPolicy.deposit,
        rentalPolicy.damageExcess,
        rentalPolicy.theftExcess,
    ].every(isFilledNumber);

    return (
        <>
            <FormSection title="Standard Deposit & Excess" level={2}>
                <Row className="g-3">
                    {REDUCTION_FIELDS.map((field) => (
                        <Col md={4} key={field.rentalPolicyField}>
                            <Form.Group>
                                <Form.Label>
                                    {field.label}{" "}
                                    <span className="text-danger">*</span>
                                </Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        step="0.01"
                                        value={
                                            rentalPolicy[
                                            field.rentalPolicyField
                                            ] ?? ""
                                        }
                                        onChange={(event) =>
                                            setFieldValue(
                                                `rentalPolicy.${field.rentalPolicyField}`,
                                                decimalOrEmpty(
                                                    event.target.value
                                                )
                                            )
                                        }
                                        placeholder="0.00"
                                    />
                                    <InputGroup.Text>€</InputGroup.Text>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    ))}
                </Row>
            </FormSection>

            <FormSection title="Collision Damage Waivers" level={2}>
                {!baseComplete && (
                    <Alert variant="warning" className="py-2 mb-4">
                        Complete the standard deposit, damage excess and theft
                        excess before enabling a waiver.
                    </Alert>
                )}

                {CDW_CONFIG.map((config) => {
                    const extraIndex = findExtraIndex(config.key);
                    const enabled = extraIndex >= 0;
                    const extra = enabled ? extras[extraIndex] : null;
                    const extraName = config.name;
                    const extraSubtitle = config.subtitle;

                    const reducedValues = REDUCTION_FIELDS.map(
                        (field) =>
                            values[
                            `${field.discountedPrefix}${config.suffix}`
                            ]
                    );

                    const missingFields =
                        enabled &&
                        [
                            Number(extra?.pricePerDay) > 0,
                            ...reducedValues.map(isFilledNumber),
                        ].some((valid) => !valid);

                    const showValidation = submitCount > 0 && missingFields;

                    return (
                        <Card className="mb-4" key={config.key}>
                            <Card.Header>
                                <div className="d-flex justify-content-between align-items-start gap-3">
                                    <div>
                                        <div className="fw-semibold">
                                            {extraName}
                                        </div>
                                        <Form.Text className="text-muted">
                                            {extraSubtitle}
                                        </Form.Text>
                                    </div>

                                    <Form.Check
                                        type="switch"
                                        id={`excessDeposit.${config.key}.enabled`}
                                        label={enabled ? "Enabled" : "Disabled"}
                                        checked={enabled}
                                        disabled={!baseComplete && !enabled}
                                        onChange={(event) =>
                                            setEnabled(
                                                config,
                                                event.target.checked
                                            )
                                        }
                                    />
                                </div>
                            </Card.Header>

                            {enabled && (
                                <Card.Body>
                                    <div className="fw-medium mb-3">
                                        Charge settings
                                    </div>

                                    <Row className="g-3 mb-4">
                                        <Col md={6} lg={4}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Daily Price{" "}
                                                    <span className="text-danger">
                                                        *
                                                    </span>
                                                </Form.Label>
                                                <InputGroup>
                                                    <CentsInput
                                                        value={
                                                            extra?.pricePerDay
                                                        }
                                                        onChange={(value) =>
                                                            setExtraField(
                                                                config.key,
                                                                "pricePerDay",
                                                                value
                                                            )
                                                        }
                                                        placeholder="e.g. 15.00"
                                                    />
                                                    <InputGroup.Text>
                                                        €/day
                                                    </InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6} lg={4}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Maximum Price
                                                </Form.Label>
                                                <InputGroup>
                                                    <CentsInput
                                                        value={extra?.maxPrice}
                                                        onChange={(value) =>
                                                            setExtraField(
                                                                config.key,
                                                                "maxPrice",
                                                                value === ""
                                                                    ? 0
                                                                    : value
                                                            )
                                                        }
                                                        placeholder="0"
                                                    />
                                                    <InputGroup.Text>
                                                        €
                                                    </InputGroup.Text>
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    0 means no maximum.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6} lg={4}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Requirement
                                                </Form.Label>
                                                <div className="pt-2">
                                                    <Form.Check
                                                        type="switch"
                                                        id={`excessDeposit.${config.key}.isRequired`}
                                                        label="Mandatory"
                                                        checked={Boolean(
                                                            extra?.isRequired
                                                        )}
                                                        onChange={(event) =>
                                                            setExtraField(
                                                                config.key,
                                                                "isRequired",
                                                                event.target
                                                                    .checked
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="fw-medium mb-1">
                                        Reduced deposit and excess
                                    </div>
                                    <Form.Text className="text-muted d-block mb-3">
                                        Enter the amounts that apply after the
                                        customer selects {extraName}.
                                    </Form.Text>

                                    <Row className="g-3 mb-4">
                                        {REDUCTION_FIELDS.map((field) => {
                                            const valueField = `${field.discountedPrefix}${config.suffix}`;
                                            const originalValue =
                                                rentalPolicy[
                                                field.rentalPolicyField
                                                ];

                                            return (
                                                <Col md={4} key={valueField}>
                                                    <Form.Group>
                                                        <Form.Label>
                                                            Reduced {field.label}{" "}
                                                            <span className="text-danger">
                                                                *
                                                            </span>
                                                        </Form.Label>
                                                        <InputGroup>
                                                            <Form.Control
                                                                type="number"
                                                                inputMode="decimal"
                                                                min={0}
                                                                step="0.01"
                                                                value={
                                                                    values[
                                                                    valueField
                                                                    ] ?? ""
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    setFieldValue(
                                                                        valueField,
                                                                        decimalOrEmpty(
                                                                            event
                                                                                .target
                                                                                .value
                                                                        )
                                                                    )
                                                                }
                                                                placeholder="0.00"
                                                            />
                                                            <InputGroup.Text>
                                                                €
                                                            </InputGroup.Text>
                                                        </InputGroup>
                                                        <Form.Text className="text-muted">
                                                            Standard: €
                                                            {originalValue ?? 0}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                            );
                                        })}
                                    </Row>

                                    <Form.Group>
                                        <Form.Label>
                                            Booking Description
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={extra?.description ?? ""}
                                            onChange={(event) =>
                                                setExtraField(
                                                    config.key,
                                                    "description",
                                                    event.target.value || null
                                                )
                                            }
                                            placeholder={`Optional description shown for ${extraName}`}
                                        />
                                    </Form.Group>

                                    {showValidation && (
                                        <Alert
                                            variant="danger"
                                            className="py-2 mt-3 mb-0"
                                        >
                                            Complete the price and all three
                                            reduced amounts before saving.
                                        </Alert>
                                    )}
                                </Card.Body>
                            )}
                        </Card>
                    );
                })}
            </FormSection>
        </>
    );
}